import { collection, doc, getDoc, getDocs, limit, onSnapshot, query, where } from 'firebase/firestore';
import { deleteObject, ref, uploadBytesResumable } from 'firebase/storage';
import {
  docExtension,
  newMediaId,
  opportunityDocPath,
  opportunityPhotoPath,
  OPPORTUNITY_LIMITS,
  type AddOpportunityFileInput,
  type CreateOpportunityInput,
  type EditOpportunityItemInput,
  type OpportunityDoc,
  type SaveOpportunityInput,
  type ShareOpportunityInput,
  type StoredFile,
} from '@blauturquesa/shared';
import { db } from '../../firebase/firestore';
import { storage } from '../../firebase/storage';
import { call } from '../../firebase/functions';
import { options, usingEmulators } from '../../firebase/config';

export interface OpportunityRow extends OpportunityDoc {
  id: string;
}

const MAX_LIST = 300;

/**
 * Oportunitats que es poden veure (§8.8), sota demanda. Un agent, amb el `where` que exigeixen les
 * regles (§20.1); el superadmin, totes.
 */
export async function listOpportunities(uid: string, isSuperadmin: boolean): Promise<{ rows: OpportunityRow[]; truncated: boolean }> {
  const col = collection(db, 'opportunities');
  const q = isSuperadmin ? query(col, limit(MAX_LIST)) : query(col, where('accessUids', 'array-contains', uid), limit(MAX_LIST));
  const snap = await getDocs(q);
  return { rows: snap.docs.map((d) => ({ id: d.id, ...(d.data() as OpportunityDoc) })), truncated: snap.size === MAX_LIST };
}

/** L'oportunitat oberta, en temps real: un sol document (§3.2, escoltes només sobre el que és obert). */
export function watchOpportunity(id: string, onData: (row: OpportunityRow | null) => void, onError: () => void): () => void {
  return onSnapshot(doc(db, 'opportunities', id), (snap) => onData(snap.exists() ? { id: snap.id, ...(snap.data() as OpportunityDoc) } : null), onError);
}

export interface ModuleMember { uid: string; name: string; email: string }

export async function loadMembers(): Promise<ModuleMember[]> {
  const snap = await getDoc(doc(db, 'moduleMembers', 'opportunities'));
  const members = (snap.data()?.['members'] ?? {}) as Record<string, { name?: string; email?: string }>;
  return Object.entries(members)
    .map(([uid, m]) => ({ uid, name: m.name ?? '', email: m.email ?? '' }))
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
}

export const createOpportunity = (input: CreateOpportunityInput) => call<CreateOpportunityInput, { id: string }>('createOpportunity', input);
export const saveOpportunity = (input: SaveOpportunityInput) => call<SaveOpportunityInput, { version: number }>('saveOpportunity', input);
export const editItem = (input: EditOpportunityItemInput) => call<EditOpportunityItemInput, { itemId: string }>('editOpportunityItem', input);
export const shareOpportunity = (input: ShareOpportunityInput) => call<ShareOpportunityInput, { ok: true }>('shareOpportunity', input);
export const deleteOpportunity = (input: { id: string; confirmTitle: string }) => call<typeof input, { ok: true }>('deleteOpportunity', input);

/**
 * URL d'un fitxer privat amb el seu testimoni de descàrrega. Només la coneix qui pot llegir l'oportunitat
 * (el testimoni és dins el document). No cal cap crida per obrir-lo.
 */
export function fileUrl(f: StoredFile): string {
  const base = usingEmulators ? 'http://127.0.0.1:9199' : 'https://firebasestorage.googleapis.com';
  return `${base}/v0/b/${options.storageBucket ?? ''}/o/${encodeURIComponent(f.path)}?alt=media&token=${encodeURIComponent(f.token)}`;
}

export class UploadError extends Error {
  constructor(public readonly reason: 'notImage' | 'tooLarge' | 'badType') {
    super(reason);
  }
}

type Part = { path: string; contentType: string; bytes: number };

async function put(path: string, blob: Blob, contentType: string, onBytes: (n: number) => void): Promise<Part> {
  const task = uploadBytesResumable(ref(storage, path), blob, { contentType });
  task.on('state_changed', (s) => onBytes(s.bytesTransferred));
  await task;
  return { path, contentType, bytes: blob.size };
}

/** Si el registre falla, s'esborra el que s'ha pujat perquè no quedin fitxers orfes. */
async function withCleanup<T>(paths: string[], run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    await Promise.all(paths.map((p) => deleteObject(ref(storage, p)).catch(() => undefined)));
    throw err;
  }
}

/** Foto: dues mides (miniatura i gran), redimensionades al navegador, amb l'orientació del mòbil. */
export async function uploadPhoto(oppId: string, file: File, onProgress: (p: number) => void): Promise<void> {
  const [thumb, full] = await Promise.all([resize(file, 480), resize(file, 1600)]);
  const fileId = newMediaId();
  const stamp = Date.now();
  const sent = [0, 0];
  const total = thumb.blob.size + full.blob.size;
  const report = () => onProgress((sent[0]! + sent[1]!) / total);
  const fullPath = opportunityPhotoPath(oppId, fileId, stamp, 1600, full.type);
  const thumbPath = opportunityPhotoPath(oppId, fileId, stamp, 480, thumb.type);
  await withCleanup([fullPath, thumbPath], async () => {
    const fullPart = await put(fullPath, full.blob, full.type, (n) => { sent[0] = n; report(); });
    const thumbPart = await put(thumbPath, thumb.blob, thumb.type, (n) => { sent[1] = n; report(); });
    await call<AddOpportunityFileInput, { fileId: string }>('addOpportunityFile', {
      kind: 'photo', id: oppId, fileId, name: file.name.slice(0, 200), description: '', full: fullPart, thumb: thumbPart,
    });
  });
}

/** Document: tal com és, amb la descripció. */
export async function uploadDoc(oppId: string, file: File, description: string, onProgress: (p: number) => void): Promise<void> {
  const ext = docExtension(file.name);
  if (!ext) throw new UploadError('badType');
  if (file.size > OPPORTUNITY_LIMITS.docBytes) throw new UploadError('tooLarge');
  const fileId = newMediaId();
  const path = opportunityDocPath(oppId, fileId, Date.now(), ext);
  await withCleanup([path], async () => {
    const part = await put(path, file, file.type || 'application/octet-stream', (n) => onProgress(n / file.size));
    await call<AddOpportunityFileInput, { fileId: string }>('addOpportunityFile', {
      kind: 'doc', id: oppId, fileId, name: file.name.slice(0, 200), description, file: part,
    });
  });
}

async function resize(file: File, maxWidth: number): Promise<{ blob: Blob; type: 'image/webp' | 'image/jpeg' }> {
  if (!file.type.startsWith('image/')) throw new UploadError('notImage');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new UploadError('notImage');
  }
  try {
    const width = Math.min(maxWidth, bitmap.width);
    const height = Math.round((bitmap.height * width) / bitmap.width);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new UploadError('notImage');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    const toBlob = (type: string, q: number) => new Promise<Blob | null>((r) => canvas.toBlob(r, type, q));
    let blob = await toBlob('image/webp', 0.82);
    let type: 'image/webp' | 'image/jpeg' = 'image/webp';
    if (!blob || blob.type !== 'image/webp') {
      blob = await toBlob('image/jpeg', 0.85);
      type = 'image/jpeg';
    }
    if (!blob) throw new UploadError('notImage');
    if (blob.size > OPPORTUNITY_LIMITS.photoBytes) throw new UploadError('tooLarge');
    return { blob, type };
  } finally {
    bitmap.close();
  }
}
